//@ts-check
import { actionTypes } from 'redux-form';
import * as _ from 'lodash';

import * as DuplicateAppTypes from 'Pages/reviewincomingapplications/state/duplicateapps/types';

import * as types from './types';
import { formatReleaseCategories } from '../../helpers/HouseholdExceptionHelpers';
import { STUDENTLOOKUP_ADD_STUDENT } from '../studentlookup/types';
import {
  getFamilySize,
  modifyMembersForEmancipatedMinor,
  getShouldRecalculateApp,
  getPropertyFromFormName,
  getStateAfterRemovingIncome,
  getStateAfterClearingCaseInfo,
  getStateWithModifiedSubmitErrors,
} from '../../helpers/Helpers';
import {
  SecondReviewResultEnumeration,
  NEW_STUDENT,
} from '../../helpers/Constants.js';
import {
  APPLICATION_HUB_CONTACT_INFO_FORM_NAME,
  APPLICATION_HUB_APP_INFO_FORM_NAME,
  APPLICATION_HUB_HEADER_FORM_NAME,
} from 'Common/FormEnum';
import { ApplicationFieldEnum as Fields } from 'Common/FieldEnum';

const INITIAL_STATE = {
  releaseCategoryLookup: [],
  dbApplication: {},
  tempApplication: {},
  fetching: false,
  fetchError: null,
  shouldRecalculateApp: false,
  appRecalculated: false,
  submitting: false,
  submitted: false,
  submitErrors: null,
  shouldRedirect: false,
  copyWarnings: null,
  isSecondReview: false,
  isVerification: false,
  hasStatusChanged: false,
  originalStatusCurrent: null,
  originalStatusFuture: null,
  showSecondReviewStatusChangeDialog: false,
  secondReviewResultRecorded: false,
  hasReimbursableMeals: false,
  reprocessingMealsLoading: false,
  reprocessSuccessful: null,
  isSelectedForVerification: false,
  verificationSelectionLetterPrinted: false,
  isDuplicateApp: false,
};

const watchedForms = [
  APPLICATION_HUB_CONTACT_INFO_FORM_NAME,
  APPLICATION_HUB_APP_INFO_FORM_NAME,
  APPLICATION_HUB_HEADER_FORM_NAME,
];

export default (state = INITIAL_STATE, action) => {
  const { type, payload } = action;

  switch (type) {
    case actionTypes.CHANGE: {
      let tempPayload = _.cloneDeep(payload);
      //keeps the tempApplication in sync with the redux-form state
      const { form, field } = action.meta;

      //On the frontend you are only ever changing the most recent note
      //can only modify when notes are null (new app most likely)
      if (field === Fields.Notes) {
        if (
          state.tempApplication.ApplicationInformation.Notes === null ||
          state.tempApplication.ApplicationInformation.Notes.length === 0
        ) {
          tempPayload = [{ Id: 0, Description: tempPayload, ReadOnly: 'N' }];
        } else {
          let temp = _.cloneDeep(
            state.tempApplication.ApplicationInformation.Notes
          );
          temp[0].Description = tempPayload;
          tempPayload = temp;
        }
      }

      if (watchedForms.includes(form)) {
        const prop = getPropertyFromFormName[form];
        let result = null;

        if (
          field === Fields.SecondReviewMarkAsIncomplete ||
          field === Fields.SecondReviewMarkAsNoChange ||
          field === Fields.SecondReviewCategoricalEligibilityError ||
          field === Fields.SecondReviewGrossIncomeCalculationError ||
          field === Fields.SecondReviewOtherError
        ) {
          if (payload) {
            switch (field) {
              case Fields.SecondReviewMarkAsIncomplete:
                result =
                  SecondReviewResultEnumeration.IncompleteApplicationError;
                break;

              case Fields.SecondReviewMarkAsNoChange:
                result = SecondReviewResultEnumeration.NoChange;
                break;

              case Fields.SecondReviewGrossIncomeCalculationError:
                result =
                  SecondReviewResultEnumeration.GrossIncomeCalculationError;
                break;

              case Fields.SecondReviewCategoricalEligibilityError:
                result =
                  SecondReviewResultEnumeration.CategoricalEligibilityError;
                break;
              case Fields.SecondReviewOtherError:
                result = SecondReviewResultEnumeration.OtherError;
                break;
              default:
                result = null;
            }
          }

          let nextState = {
            ...state,
            tempApplication: {
              ...state.tempApplication,
              [prop]: {
                ...state.tempApplication[prop],
                SecondReviewResultId: result,
              },
            },
          };
          return nextState;
        }

        let nextState = {
          ...state,
          tempApplication: {
            ...state.tempApplication,
            [prop]: {
              ...state.tempApplication[prop],
              [field]: tempPayload,
            },
          },
        };

        return nextState;
      }

      return state;
    }

    case types.APPLICATION_HUB_FORCE_RECALCULATE:
      //this only works if the field is added to the manually recalculated arrary
      //otherwise there is a race condition with the CHANGE type
      return {
        ...state,
        shouldRecalculateApp: true,
      };

    case types.APPLICATION_HUB_CLEAR:
      return {
        ...INITIAL_STATE,
      };

    case types.APPLICATION_HUB_FETCHING:
      return {
        ...state,
        fetching: payload,
      };

    case types.APPLICATION_HUB_SUBMITTING:
      return {
        ...state,
        submitting: payload,
        submitted: false,
      };

    case types.APPLICATION_HUB_SUBMITTED:
      return {
        ...state,
        dbApplication: payload,
        tempApplication: _.cloneDeep(payload),
        submitted: true,
      };

    case types.APPLICATION_HUB_SUBMIT_ERROR:
      return getStateWithModifiedSubmitErrors(state, payload);

    case types.APPLICATION_HUB_CLEAR_ERRORS:
      return {
        ...state,
        fetchError: null,
        submitErrors: null,
        shouldRedirect: false,
      };

    case types.APPLICATION_HUB_FETCH_APPLICATION:
      if (payload.isCopying) {
        return {
          ...state,
          dbApplication: payload.Data.app,
          tempApplication: _.cloneDeep(payload.Data.app),
          copyWarnings:
            payload.Data.warnings.length > 0 ? payload.Data.warnings : null,
        };
      }

      return {
        ...state,
        dbApplication: payload.Data,
        tempApplication: _.cloneDeep(payload.Data),
        originalStatusCurrent: _.get(payload, 'Data.Students[0].StatusCurrent'),
        originalStatusFuture: _.get(payload, 'Data.Students[0].StatusFuture'),
      };

    case types.APPLICATION_HUB_FETCH_RELEASE_CATEGORY:
      return {
        ...state,
        releaseCategoryLookup: formatReleaseCategories(payload.data),
      };

    case types.APPLICATION_HUB_FETCH_ERROR:
      if (_.has(payload, 'response.data.Errors')) {
        const fetchError = payload.response.data.Errors[0];

        return {
          ...state,
          fetchError,
          shouldRedirect: payload.response.status === 404,
        };
      }

      return {
        ...state,
        fetchError: {
          Title: 'Error',
          Message:
            'An error occurred while fetching this application. Please try again.',
        },
      };
    case types.APPLICATION_HUB_REDIRECT:
      return { ...state, shouldRedirect: true };

    case types.APPLICATION_HUB_APP_RECALCULATED:
      return {
        ...state,
        appRecalculated: payload,
      };

    case types.APPLICATION_HUB_FETCH_UPDATED_APP:
      return {
        ...state,
        tempApplication: payload.data,
        shouldRecalculateApp: false,
      };

    case types.APPLICATION_HUB_UPDATE_HOH: {
      let members = _.cloneDeep(state.tempApplication.Members);
      const familySize = _.get(state, 'tempApplication.ApplicationInformation.FamilySize', 0);
      let membersAdded = 0;
      const indexOfHoh = members.findIndex((x) => x.IsHOH);
      payload.IsHOH = true;
      if (indexOfHoh !== -1) {
        members[indexOfHoh] = payload;
      } else {
        members.push(payload);
        membersAdded = 1;
      }

      let nextState = {
        ...state,
        tempApplication: {
          ...state.tempApplication,
          ApplicationInformation: {
            ...state.tempApplication.ApplicationInformation,
            LanguageId: payload.LanguageId,
            FamilySize: familySize + membersAdded,
          },
          Members: members,
        },
      };

      nextState.shouldRecalculateApp = getShouldRecalculateApp(
        state.tempApplication,
        nextState.tempApplication
      );

      return nextState;
    }

    case types.APPLICATION_HUB_UPDATE_MEMBER: {
      let members = _.cloneDeep(state.tempApplication.Members);
      const familySize = _.get(state, 'tempApplication.ApplicationInformation.FamilySize', 0);
      let membersAdded = 0;
      const indexOfMember = members.findIndex((x) => x.Id === payload.Id);
      payload.IsHOH = false;
      if (indexOfMember !== -1) {
        members[indexOfMember] = payload;
      } else {
        members.push(payload);
        membersAdded = 1;
      }

      let nextState = {
        ...state,
        tempApplication: {
          ...state.tempApplication,
          ApplicationInformation: {
            ...state.tempApplication.ApplicationInformation,
            FamilySize: familySize + membersAdded,
          },
          Members: members,
        },
      };
      nextState.shouldRecalculateApp = getShouldRecalculateApp(
        state.tempApplication,
        nextState.tempApplication
      );

      return nextState;
    }

    case types.APPLICATION_HUB_UPDATE_STUDENT: {
      let students = _.cloneDeep(state.tempApplication.Students);
      let members = _.cloneDeep(state.tempApplication.Members);

      const previousId = payload.previousId || NEW_STUDENT[Fields.Id];
      let studentId = payload.student.Id;
      const idSeed = payload.student.IdSeed;
      let indexOfStudent = -1;

      // Handle the possibility of the student's id being taken if its a duplicate application being edited
      if (previousId !== studentId && idSeed === 0) {
        indexOfStudent = students.findIndex(
          (x) => x.Id === previousId && x.IdSeed === idSeed
        );
      } else {
        indexOfStudent = students.findIndex((x) => x.Id === studentId);
      }

      if (indexOfStudent !== -1) {
        students[indexOfStudent] = payload.student;
      } else {
        students.push(payload.student);
      }
      let result = modifyMembersForEmancipatedMinor(students, members);
      let nextState = {
        ...state,
        tempApplication: {
          ...state.tempApplication,
          Students: result.students,
          Members: result.members,
        },
      };

      nextState.shouldRecalculateApp =
        payload.isNewStudent ||
        getShouldRecalculateApp(
          state.tempApplication,
          nextState.tempApplication
        );

      return nextState;
    }

    case types.APPLICATION_HUB_CHANGE_EFFECTIVE_DATE:
      return {
        ...state,
        shouldRecalculateApp: true,
        tempApplication: {
          ...state.tempApplication,
          Students: state.tempApplication.Students.map((student) => {
            if (student[Fields.Id] !== payload.id) {
              return student;
            }

            return {
              ...student,
              [Fields.EffectiveDate]: payload.date,
              [Fields.EffectiveDateHasChanged]: true,
            };
          }),
        },
      };

    case types.APPLICATION_HUB_CHANGE_APPLICATION_DATE:
      return {
        ...state,
        shouldRecalculateApp: payload.recalculate,
        tempApplication: {
          ...state.tempApplication,
          ApplicationInformation: {
            ...state.tempApplication.ApplicationInformation,
            [Fields.ApplicationDate]: payload.date,
          },
        },
      };

    case types.APPLICATION_HUB_DELETE_MEMBER: {
      const familySize = _.get(state, 'tempApplication.ApplicationInformation.FamilySize', 0);
      return {
        ...state,
        tempApplication: {
          ...state.tempApplication,
          ApplicationInformation: {
            ...state.tempApplication.ApplicationInformation,
            FamilySize: familySize - 1,
          },
          Members: state.tempApplication.Members.filter(
            (x) => x.Id !== payload.Id
          ),
        },
      };
    }

    case types.APPLICATION_HUB_DELETE_STUDENT: {
      //remove the deleted student from the collection
      const updatedStudents = state.tempApplication.Students.filter(
        (x) => x.Id !== payload
      );
      const result = modifyMembersForEmancipatedMinor(
        updatedStudents,
        state.tempApplication.Members
      );
      const familySize = _.get(state, 'tempApplication.ApplicationInformation.FamilySize', 0);

      const nextState = {
        ...state,
        shouldRecalculateApp: true,
        tempApplication: {
          ...state.tempApplication,
          ApplicationInformation: {
            ...state.tempApplication.ApplicationInformation,
            FamilySize: familySize - 1,
          },
          Students: result.students,
          Members: result.members,
        },
      };
      return nextState;
    }

    case types.APPLICATION_HUB_UPDATE_APPSUMMARY_FAMILYSIZE:
      return {
        ...state,
        tempApplication: {
          ...state.tempApplication,
          ApplicationInformation: {
            ...state.tempApplication.ApplicationInformation,
            FamilySize: getFamilySize(state.tempApplication),
          },
        },
      };

    case STUDENTLOOKUP_ADD_STUDENT: {
      const students = _.get(state, 'tempApplication.Students', []);
      const familySize = _.get(state, 'tempApplication.ApplicationInformation.FamilySize', 0);
      const tempApplication = {
        ...state.tempApplication,
        ApplicationInformation: {
          ...state.tempApplication.ApplicationInformation,
          FamilySize: familySize + 1,
        },
        Students: [...students, { ...payload, [Fields.IsNewToApp]: true }],
      };
      return {
        ...state,
        shouldRecalculateApp: true,
        tempApplication,
      };
    }

    case types.APPLICATION_HUB_CLEAR_CASE_INFO:
      return getStateAfterClearingCaseInfo(state);

    case types.APPLICATION_HUB_REMOVE_INCOME:
      return getStateAfterRemovingIncome(state, payload);

    case types.APPLICATION_HUB_SET_IS_SECOND_REVIEW:
      return { ...state, isSecondReview: payload };

    case types.APPLICATION_HUB_SET_SHOW_SECOND_REVIEW_STATUS_CHANGE_DIALOG:
      return { ...state, showSecondReviewStatusChangeDialog: payload };

    case types.APPLICATION_HUB_SET_ORIGINAL_STATUSES:
      return {
        ...state,
        originalStatusCurrent: payload.originalstatusCurrent,
        originalStatusFuture: payload.originalStatusFuture,
      };
    case types.APPLICATION_HUB_SET_HAS_STATUS_CHANGED:
      return { ...state, hasStatusChanged: payload };

    case types.APPLICATION_HUB_SET_SECOND_REVIEW_RESULT_RECORDED:
      return { ...state, secondReviewResultRecorded: payload };
    case types.APPLICATION_HUB_SET_HAS_REIMBURSABLE_MEALS:
      return {
        ...state,
        hasReimbursableMeals: payload,
      };
    case types.APPLICATION_HUB_RESET_HAS_REIMBURSABLE_MEALS:
      return {
        ...state,
        hasReimbursableMeals: null,
      };

    case types.APPLICATION_HUB_WRITE_REPROCESS_LOADING:
      return {
        ...state,
        reprocessingMealsLoading: payload,
      };

    case types.APPLICATION_HUB_WRITE_REPROCESS_SAVE_SUCCESSFUL:
      return {
        ...state,
        reprocessSuccessful: payload,
      };

    case types.APPLICATION_HUB_SET_IS_VERIFICATION:
      return { ...state, isVerification: payload };
    case types.APPLICATION_HUB_SET_VERIFICATION_SELECTION_LETTER_PRINTED:
      return {
        ...state,
        verificationSelectionLetterPrinted: payload,
      };
    case types.APPLICATION_HUB_SET_IS_SELECTED_FOR_VERIFICATION:
      return { ...state, isSelectedForVerification: payload };

    case DuplicateAppTypes.DUPLICATE_APPS_EDIT_APPLICATION:
    case DuplicateAppTypes.DUPLICATE_APPS_START_NEW_APPLICATION:
      return {
        ...state,
        dbApplication: _.cloneDeep(payload),
        tempApplication: _.cloneDeep(payload),
        isDuplicateApp: true,
      };
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/pages/manageapplication/state/hub/reducer.js